import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { Plus, Trash2, Mail, AlertCircle, CheckCircle2, Wallet, Landmark, LayoutGrid, Compass, Phone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PaymentGatewaysSection from "../components/PaymentGatewaysSection";
import TeamSection from "../components/TeamSection";
import NotificationPreferencesSection from "../components/NotificationPreferencesSection";
import BrandLogoSection from "../components/BrandLogoSection";
import ProfilePictureSection from "../components/ProfilePictureSection";
import CompAutoRefreshSection from "../components/CompAutoRefreshSection";
import EsignatureSection from "../components/EsignatureSection";
import EmailAccountSection from "../components/EmailAccountSection";
import SecuritySection from "../components/SecuritySection";
import HealthBanner from "../components/HealthBanner";
import { restartTour, isTourDisabled, setTourDisabled } from "../components/ProductTour";
import FeatureTour from "../components/FeatureTour";
import { useAuth } from "../context/AuthContext";

const SETTINGS_TOUR_STEPS = [
  {
    target: '[data-testid="emergency-contact-section"]',
    placement: "top",
    title: "🚨 Emergency Maintenance Contact",
    content:
      "Drop a 24/7-monitored phone number here. The moment a tenant submits a work order tagged Emergency, that number gets a real SMS — burst pipes, gas leaks, no-heat-in-winter calls reach you instantly.",
    disableBeacon: true,
  },
  {
    target: '[data-testid="email-signature"]',
    placement: "top",
    title: "Email signature",
    content:
      "Auto-appended to every email you send from RealtyVault — replies, tenant invites, year-end binders sent to your CPA. Plain text, ≤2000 chars, renders cleanly in Gmail/Outlook.",
    disableBeacon: true,
  },
  {
    target: '[data-testid="sms-test-row"]',
    placement: "top",
    title: "Test your SMS pipeline",
    content:
      "Send a one-off SMS to verify Twilio + your phone formatting before you rely on it for rent reminders or emergency dispatch. Bypasses notification preferences so you always see a result.",
    disableBeacon: true,
  },
];

export default function Settings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [emails, setEmails] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [acceptRent, setAcceptRent] = useState(true);
  const [acceptLoan, setAcceptLoan] = useState(true);
  const [loansModule, setLoansModule] = useState(true);
  const [digestEnabled, setDigestEnabled] = useState(true);
  const [emergencyPhone, setEmergencyPhone] = useState("");
  const [emergencyPhoneSaved, setEmergencyPhoneSaved] = useState("");
  const [emergencyBusy, setEmergencyBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/user/settings");
    setEmails(data.stakeholder_emails || []);
    setAcceptRent(data.accept_rent_payments !== false);
    setAcceptLoan(data.accept_loan_payments !== false);
    setLoansModule(data.loans_module_enabled !== false);
    setDigestEnabled(data.monthly_valuation_digest_enabled !== false);
    setEmergencyPhone(data.emergency_maintenance_phone || "");
    setEmergencyPhoneSaved(data.emergency_maintenance_phone || "");
  };
  useEffect(() => { load(); }, []);

  // Auto-scroll to the section indicated by the URL hash on first render so
  // links like `/dashboard/settings#email-account` (e.g. the BYO SMTP nudge
  // on the dashboard) land the user directly on the right section.
  useEffect(() => {
    if (!window.location.hash) return;
    const id = window.location.hash.slice(1);
    // Wait one frame so the section has been rendered.
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  const saveEmergencyPhone = async () => {
    setEmergencyBusy(true);
    try {
      const { data } = await api.put("/user/settings", { emergency_maintenance_phone: emergencyPhone.trim() });
      const saved = data.emergency_maintenance_phone || "";
      setEmergencyPhone(saved);
      setEmergencyPhoneSaved(saved);
      if (saved) {
        toast.success(`Emergency contact saved: ${saved}`);
      } else {
        toast.success("Emergency contact cleared");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setEmergencyBusy(false);
    }
  };

  const toggleAccept = async (key, next) => {
    // Optimistic update
    if (key === "accept_rent_payments") setAcceptRent(next);
    if (key === "accept_loan_payments") setAcceptLoan(next);
    if (key === "loans_module_enabled") setLoansModule(next);
    if (key === "monthly_valuation_digest_enabled") setDigestEnabled(next);
    try {
      await api.put("/user/settings", { [key]: next });
      const label = {
        accept_rent_payments: "Rent payments",
        accept_loan_payments: "Loan payment entries",
        loans_module_enabled: "Loans module",
        monthly_valuation_digest_enabled: "Monthly valuation digest",
      }[key];
      toast.success(`${label} ${next ? "enabled" : "disabled"}`);
      // When toggling the loans module, reload the page so the sidebar
      // recomputes from the fresh /auth/me → user.loans_module_enabled.
      if (key === "loans_module_enabled") {
        setTimeout(() => window.location.reload(), 600);
      }
    } catch (e) {
      // Revert on failure
      if (key === "accept_rent_payments") setAcceptRent(!next);
      if (key === "accept_loan_payments") setAcceptLoan(!next);
      if (key === "loans_module_enabled") setLoansModule(!next);
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const add = () => {
    const v = input.trim().toLowerCase();
    if (!v || !v.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    if (emails.includes(v)) {
      toast.error("Already on the list");
      return;
    }
    setEmails([...emails, v]);
    setInput("");
  };

  const remove = (e) => setEmails(emails.filter((x) => x !== e));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/user/settings", { stakeholder_emails: emails });
      toast.success("Stakeholder list saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const testRedZone = async () => {
    setTestBusy(true);
    try {
      const { data } = await api.post("/notifications/test-red-zone");
      const res = data.result || {};
      if (res.status === "sent") toast.success("Test email sent via Resend");
      else if (res.status === "mocked") toast.message("Email service in MOCK mode — configure RESEND_API_KEY to actually send", { description: `Would send to: ${res.recipients?.join(", ")}` });
      else if (res.status === "no_red_alerts") toast.message("No critical (≤30d) loans right now", { description: "Add a loan with maturity within 30 days to test." });
      else toast.error(res.error || res.reason || "Unknown result");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setTestBusy(false); }
  };

  const testWeekly = async () => {
    setTestBusy(true);
    try {
      const { data } = await api.post("/reports/maturity/send-weekly");
      const res = data.result || {};
      if (res.status === "sent") toast.success("Weekly PDF sent");
      else if (res.status === "mocked") toast.message("MOCK — no API key configured");
      else if (res.status === "no_alerts_this_week") toast.message("No events in next 90 days — nothing to send");
      else toast.error(res.error || res.reason || "Unknown result");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setTestBusy(false); }
  };

  return (
    <DashboardShell>
      <FeatureTour
        featureId="settings-v2"
        steps={SETTINGS_TOUR_STEPS}
        waitForSelector='[data-testid="emergency-contact-section"]'
        delayMs={900}
      />
      <div className="p-8 max-w-5xl">
        <div className="mb-6">
          <div className="overline text-slate-500">Workspace</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily:"Outfit"}}>Settings</h1>
          <p className="text-sm text-slate-500 mt-1">Configure stakeholder notifications, scheduled reports, and payment gateways.</p>
        </div>

        <HealthBanner />

        <ProfilePictureSection />

        <SecuritySection />

        <CompAutoRefreshSection />

        <PaymentGatewaysSection />

        <div id="email-account">
          <EmailAccountSection />
        </div>

        <EsignatureSection />

        <BrandLogoSection />

        <TeamSection />

        <section className="bg-white border border-slate-200 rounded-sm p-6 mb-6" data-testid="payment-acceptance-section">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-9 h-9 bg-emerald-600 text-white flex items-center justify-center rounded-sm"><Wallet className="w-4 h-4" /></div>
            <div>
              <h2 className="text-lg font-bold text-slate-900" style={{fontFamily:"Outfit"}}>Payment Acceptance</h2>
              <p className="text-sm text-slate-500">Master on/off switches for the payment flows in your account. Use these to freeze inbound payments during year-end reconciliation, disputes, or account migration.</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <PaymentToggle
              icon={<Wallet className="w-4 h-4" />}
              title="Accept rent payments online"
              description="When off, tenants see their Pay Rent button disabled with a friendly message and cannot initiate Stripe checkout."
              testid="toggle-accept-rent"
              enabled={acceptRent}
              onChange={(v) => toggleAccept("accept_rent_payments", v)}
            />
            <PaymentToggle
              icon={<Landmark className="w-4 h-4" />}
              title="Allow loan payment entries"
              description="When off, you can't record new loan payments (principal/interest) in the Loans module. Existing entries remain editable."
              testid="toggle-accept-loan"
              enabled={acceptLoan}
              onChange={(v) => toggleAccept("accept_loan_payments", v)}
            />
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-sm p-6 mb-6" data-testid="modules-section">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-9 h-9 bg-slate-900 text-white flex items-center justify-center rounded-sm"><LayoutGrid className="w-4 h-4" /></div>
            <div>
              <h2 className="text-lg font-bold text-slate-900" style={{fontFamily:"Outfit"}}>Modules</h2>
              <p className="text-sm text-slate-500">Hide feature modules you don't need. Your data is preserved — you can turn any module back on at any time.</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <PaymentToggle
              icon={<Landmark className="w-4 h-4" />}
              title="Loans module"
              description="When off, the Loans sidebar item and page are hidden. Loan records, payments, and alert history stay intact."
              testid="toggle-loans-module"
              enabled={loansModule}
              onChange={(v) => toggleAccept("loans_module_enabled", v)}
            />
            <PaymentToggle
              icon={<LayoutGrid className="w-4 h-4" />}
              title="Monthly valuation digest"
              description="Auto-emailed on the 1st of each month — portfolio value, month-over-month delta, per-property diffs. Toggle off to unsubscribe (scheduled job will skip you + manual triggers will no-op)."
              testid="toggle-monthly-digest"
              enabled={digestEnabled}
              onChange={(v) => toggleAccept("monthly_valuation_digest_enabled", v)}
            />
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-sm p-6 mb-6" data-testid="emergency-contact-section">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-9 h-9 bg-rose-600 text-white flex items-center justify-center rounded-sm"><Phone className="w-4 h-4" /></div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-slate-900" style={{fontFamily:"Outfit"}}>Emergency Maintenance Contact</h2>
              <p className="text-sm text-slate-500">
                When a tenant submits a work order with priority <strong className="text-rose-700">Emergency</strong>, we'll instantly text this number with the tenant's name, property, unit, and issue summary. Use a 24/7-monitored line — your on-call manager, your superintendent's mobile, or a dedicated emergency phone.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[240px]">
              <label className="block text-[11px] uppercase tracking-wider text-slate-600 mb-1" style={{ fontFamily: "Outfit" }}>
                Emergency contact number
              </label>
              <input
                type="tel"
                className="input-clean w-full font-mono"
                placeholder="+13365551234 or (336) 555-1234"
                value={emergencyPhone}
                onChange={(e) => setEmergencyPhone(e.target.value)}
                data-testid="emergency-phone-input"
              />
            </div>
            <button
              type="button"
              onClick={saveEmergencyPhone}
              disabled={emergencyBusy || emergencyPhone.trim() === emergencyPhoneSaved.trim()}
              className="btn-primary-dark inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="emergency-phone-save"
            >
              {emergencyBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
              {emergencyBusy ? "Saving…" : "Save"}
            </button>
            {emergencyPhoneSaved ? (
              <button
                type="button"
                onClick={() => { setEmergencyPhone(""); setTimeout(saveEmergencyPhone, 0); }}
                disabled={emergencyBusy}
                className="text-xs font-semibold text-rose-700 hover:text-rose-900 inline-flex items-center gap-1"
                data-testid="emergency-phone-clear"
              >
                <Trash2 className="w-3 h-3" /> Clear
              </button>
            ) : null}
          </div>

          {emergencyPhoneSaved ? (
            <div className="mt-3 text-xs text-emerald-700 inline-flex items-center gap-1.5" data-testid="emergency-phone-active">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Active. Saved as <span className="font-mono font-semibold">{emergencyPhoneSaved}</span> · texts dispatch via your platform Twilio integration.
            </div>
          ) : (
            <div className="mt-3 text-xs text-slate-500 inline-flex items-center gap-1.5" data-testid="emergency-phone-inactive">
              <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
              No emergency contact set — emergency-priority work orders will only generate the standard email notification.
            </div>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-9 h-9 bg-slate-900 text-white flex items-center justify-center rounded-sm"><Mail className="w-4 h-4" /></div>
            <div>
              <h2 className="text-lg font-bold text-slate-900" style={{fontFamily:"Outfit"}}>Stakeholder Emails</h2>
              <p className="text-sm text-slate-500">These addresses receive copies of red-zone loan alerts and the weekly maturity PDF, in addition to the account owner.</p>
            </div>
          </div>

          <div className="flex gap-2 mb-4">
            <input
              type="email"
              data-testid="stakeholder-email-input"
              className="input-clean flex-1"
              placeholder="cfo@company.com"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            />
            <button onClick={add} data-testid="add-stakeholder-button" className="btn-outline-dark flex items-center gap-2 text-sm">
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>

          {emails.length === 0 ? (
            <div className="text-sm text-slate-400 border border-dashed border-slate-200 p-6 text-center rounded-sm">
              No stakeholder emails yet. Only {/* email will render via settings */}you receive notifications.
            </div>
          ) : (
            <ul className="border border-slate-200 rounded-sm divide-y divide-slate-100" data-testid="stakeholder-list">
              {emails.map((e) => (
                <li key={e} className="px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors" data-testid={`stakeholder-${e}`}>
                  <span className="text-sm font-mono text-slate-700">{e}</span>
                  <button onClick={() => remove(e)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`remove-stakeholder-${e}`}><Trash2 className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-6 flex items-center justify-end gap-2">
            <button onClick={save} disabled={busy} data-testid="save-stakeholders-button" className="btn-primary-dark disabled:opacity-60">
              {busy ? "Saving…" : "Save recipients"}
            </button>
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-9 h-9 bg-amber-500 text-white flex items-center justify-center rounded-sm"><AlertCircle className="w-4 h-4" /></div>
            <div>
              <h2 className="text-lg font-bold text-slate-900" style={{fontFamily:"Outfit"}}>Scheduled Notifications</h2>
              <p className="text-sm text-slate-500">
                <strong>Daily 09:00 UTC:</strong> critical (≤30 day) loan alerts<br/>
                <strong>Weekly Friday 09:05 UTC:</strong> maturity PDF with 90-day horizon
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
            <button onClick={testRedZone} disabled={testBusy} data-testid="test-red-zone-button" className="btn-outline-dark text-sm disabled:opacity-60">
              Send test red-zone email now
            </button>
            <button onClick={testWeekly} disabled={testBusy} data-testid="test-weekly-button" className="btn-outline-dark text-sm disabled:opacity-60">
              Send test weekly PDF now
            </button>
          </div>
          <div className="mt-4 text-xs text-slate-500 border border-slate-200 rounded-sm p-3 bg-slate-50 flex gap-2">
            <CheckCircle2 className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
            <span>
              Email delivery requires <span className="font-mono font-bold text-slate-900">RESEND_API_KEY</span> in the backend environment.
              Without it, notifications run in <strong>mock mode</strong> (logged only).
            </span>
          </div>
        </section>

        <NotificationPreferencesSection />

        <section className="bg-white border border-slate-200 rounded-sm p-5" data-testid="product-tour-section">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-sm bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                <Compass className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="overline text-slate-500">Onboarding</div>
                <div className="text-lg font-bold text-slate-900 tracking-tight" style={{fontFamily:"Outfit"}}>Product tour</div>
                <div className="text-sm text-slate-500 mt-0.5 max-w-xl">
                  Replay the 2-minute walkthrough that highlights the five things most investors do in their first session.
                  Useful if you want to refresh your memory or show a teammate around.
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => restartTour(user?.id, navigate)}
              className="btn-outline-dark text-sm inline-flex items-center gap-2 shrink-0"
              data-testid="restart-tour-btn"
            >
              <Compass className="w-4 h-4" /> Restart tour
            </button>
          </div>
          <ProductTourToggle userId={user?.id} />
        </section>
      </div>
    </DashboardShell>
  );
}

function PaymentToggle({ icon, title, description, enabled, onChange, testid }) {
  return (
    <div className={`border rounded-sm p-4 flex items-start gap-3 transition-colors ${enabled ? "border-slate-300 bg-white" : "border-slate-200 bg-slate-50"}`} data-testid={testid}>
      <div className={`w-8 h-8 flex items-center justify-center rounded-sm shrink-0 ${enabled ? "bg-emerald-600 text-white" : "bg-slate-300 text-slate-600"}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="font-bold text-slate-900 text-sm" style={{fontFamily:"Outfit"}}>{title}</div>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            onClick={() => onChange(!enabled)}
            data-testid={`${testid}-switch`}
            className={`relative inline-flex items-center h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 ${enabled ? "bg-emerald-500" : "bg-slate-300"}`}
          >
            <span className={`inline-block w-4 h-4 bg-white rounded-full shadow transform transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">{description}</p>
        <div className="mt-2 text-[10px] font-mono uppercase tracking-widest" data-testid={`${testid}-status`}>
          {enabled ? <span className="text-emerald-700">● On</span> : <span className="text-slate-500">○ Off</span>}
        </div>
      </div>
    </div>
  );
}


function ProductTourToggle({ userId }) {
  const [enabled, setEnabled] = useState(() => !isTourDisabled(userId));

  // Re-read whenever userId resolves (avoids stale `false` during initial load)
  useEffect(() => {
    if (userId) setEnabled(!isTourDisabled(userId));
  }, [userId]);

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    setTourDisabled(userId, !next);
  };

  return (
    <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-4" data-testid="product-tour-toggle-row">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-900">Show product tour automatically</div>
        <div className="text-xs text-slate-500 mt-0.5">
          When on, the tour fires on your first <code className="font-mono text-[11px]">/dashboard</code> visit per device.
          Turn off to hide it permanently — you can still trigger it manually with “Restart tour”.
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={toggle}
        data-testid="product-tour-toggle"
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${enabled ? "bg-emerald-600" : "bg-slate-300"}`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-5" : "translate-x-0.5"}`}
        />
      </button>
    </div>
  );
}
